import * as Sentry from '@sentry/react';
import { useFlags, withLDProvider } from 'launchdarkly-react-client-sdk';
import React from 'react';

import {
  defaultFeatureFlags,
  FeatureFlagSet,
  FeatureFlagSetStruct,
} from './featureFlags';

// Exports
type Props = { children: React.ReactNode };
export const FeatureFlagProvider = ({ children }: Props) => {
  const WrappedChildren = withLDProvider({
    clientSideID: LAUNCH_DARKLY_CLIENT_SIDE_ID,
    flags: defaultFeatureFlags,
  })(() => <>{children}</>);
  return <WrappedChildren />;
};

export const useFeatureFlags = () => {
  const rawLDFlags = useFlags();
  const result = FeatureFlagSetStruct.safeParse({ ...rawLDFlags });
  if (!result.success) {
    // We haven't fetched flags from LaunchDarkly yet, so validation is expected to fail
    if (!rawLDFlags.doNotRemoveFeatureFlagProviderReady) {
      // !TODO @eldarshamukhamedov Should we return `defaultFeatureFlags` here? There's no guarantee `defaultFeatureFlags` will match defaults set in LD dashboard
      return rawLDFlags as FeatureFlagSet;
    }

    // The flags we got back from LaunchDarkly do not match what we expect (usually missing fields),
    // so we log the validation error in Sentry. We could probably throw here instead, but it's very
    // easy to break the production app by misconfiguring something in Launch Darkly.

    // eslint-disable-next-line no-console
    console.error(
      '[FeatureFlagProvider] Feature flag validation error',
      result.error,
    );
    Sentry.captureException(result.error);
    return rawLDFlags as FeatureFlagSet;
  }
  return result.data;
};
